// DEPLOY-PROBE-bump

/**
 * EmployeeService
 * 
 * Service class for managing office employee operations.
 * Provides CRUD operations, authentication, search, and password management
 * for office employees (本社 employees) in the payslip system.
 * 
 * Features:
 * - Employee authentication (login/logout)
 * - CRUD operations (create, read, update, delete)
 * - Employee search functionality
 * - Password change functionality
 * - Employee code existence checking
 * - Employee filtering by user type
 * - JWT token management
 * - Salary employee querying for payslip generation
 * 
 * Authentication:
 * - Uses JWT tokens stored in sessionStorage
 * - Automatically includes token in all authenticated requests
 * - Login stores token, logout clears token
 * 
 * Used by:
 * - UserManagementMain component for employee management
 * - Login component for authentication
 * - Various components for employee data fetching
 * 
 * @class
 */

import { EMPLOYEE_API_URL } from '../config/apiConfig';

/**
 * Employee Interface
 * 
 * Represents an office employee in the system.
 * 
 * @interface Employee
 * @property {string} empCode - Employee code (6 digits)
 * @property {string} name - Employee full name
 * @property {string} userType - User type ('従業員' or '管理者')
 * @property {string} [password] - Optional password (used for creation/updates)
 */
export interface Employee {
  empCode: string;
  name: string;
  userType: string;
  password?: string;
  /** When updating password via 従業員管理 edit: provide so DEK is re-encrypted and payslip view works after rebuild. */
  currentPassword?: string;
  /** For {@code 担当現場管理者}: genba name from construction salary data (saved in office_admin_assigned_genba). */
  assignedGenbaName?: string;
}

/**
 * Generic API Response Interface
 * 
 * Standardized response format for all API operations.
 * 
 * @interface ApiResponse
 * @template T - Type of data returned in the response
 * @property {boolean} success - Whether the operation was successful
 * @property {string} [message] - Optional message describing the result
 * @property {T} [data] - Optional response data
 * @property {boolean} [exists] - Optional flag indicating if resource exists
 */
export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  exists?: boolean;
}

/**
 * Login Response Interface
 * 
 * Response format for authentication operations.
 * 
 * @interface LoginResponse
 * @property {boolean} success - Whether login was successful
 * @property {string} [message] - Optional message describing the result
 * @property {string} [token] - JWT token if login successful
 * @property {Object} [employee] - Employee information if login successful
 * @property {string} employee.empCode - Employee code
 * @property {string} employee.name - Employee name
 * @property {string} employee.userType - User type
 */
export interface LoginResponse {
  success: boolean;
  message?: string;
  token?: string;
  employee?: {
    empCode: string;
    name: string;
    userType: string;
    /** Same empCode exists in construction_workers with matching password */
    dualAccount?: boolean;
    constructionWorkerName?: string;
    /** 担当現場管理者: assigned genba from login */
    assignedGenbaName?: string;
  };
}

/**
 * Change Password Response Interface
 * 
 * Response format for password change operations.
 * 
 * @interface ChangePasswordResponse
 * @property {boolean} success - Whether password change was successful
 * @property {string} [message] - Optional message describing the result
 */
export interface ChangePasswordResponse {
  success: boolean;
  message?: string;
}

/**
 * EmployeeService Class
 * 
 * Main service class for office employee operations.
 * Manages all API interactions for employee management.
 * 
 * @class
 */
class EmployeeService {
  /**
   * Base URL for employee API endpoints.
   * 
   * @private
   */
  private baseUrl = EMPLOYEE_API_URL;

  /**
   * Gets JWT token from sessionStorage.
   * 
   * Retrieves the authentication token stored in sessionStorage.
   * Used for authenticated API requests.
   * 
   * @private
   * @returns {string | null} JWT token or null if not found
   * 
   * @example
   * ```typescript
   * const token = this.getAuthToken();
   * // Returns token string or null
   * ```
   */
  private getAuthToken(): string | null {
    return sessionStorage.getItem('authToken');
  }

  /**
   * Gets common HTTP headers with authorization.
   * 
   * Constructs headers object with Content-Type and Authorization
   * (if token exists) for authenticated API requests.
   * 
   * @private
   * @returns {HeadersInit} Headers object with Content-Type and optional Authorization
   * 
   * @example
   * ```typescript
   * const headers = this.getHeaders();
   * // Returns { 'Content-Type': 'application/json', 'Authorization': 'Bearer ...' }
   * ```
   */
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    const token = this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  /**
   * Authenticates an employee and logs them in.
   * 
   * Sends login credentials to the server and stores the JWT token
   * in sessionStorage if authentication is successful.
   * 
   * @param empCode - Employee code (6 digits)
   * @param password - Employee password
   * @returns {Promise<LoginResponse>} Login response with token and employee info
   * @async
   * 
   * @example
   * ```typescript
   * const result = await employeeService.login('123456', 'Password123');
   * if (result.success) {
   *   // User is logged in, token stored in sessionStorage
   * }
   * ```
   */
  async login(empCode: string, password: string): Promise<LoginResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ empCode, password }),
      });

      const result = await response.json();
      
      // Store token if login is successful
      if (result.success && result.token) {
        sessionStorage.setItem('authToken', result.token);
      }
      
      return result;
    } catch (error) {
      return {
        success: false,
        message: 'ログインに失敗しました',
      };
    }
  }

  /**
   * Logs out the current employee.
   * 
   * Clears authentication token and current employee data from sessionStorage.
   * Should be called when user logs out or session expires.
   * 
   * @function
   * 
   * @example
   * ```typescript
   * employeeService.logout();
   * // Token and employee data cleared from sessionStorage
   * ```
   */
  logout(): void {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('currentEmployee');
  }

  /**
   * Creates a new employee.
   * 
   * @param employee - Employee data to create
   * @returns {Promise<ApiResponse<Employee>>} Response with created employee data
   * @async
   * 
   * @example
   * ```typescript
   * const newEmployee = {
   *   empCode: '123456',
   *   name: '山田太郎',
   *   userType: '従業員',
   *   password: 'Password123'
   * };
   * const result = await employeeService.createEmployee(newEmployee);
   * ```
   */
  async createEmployee(employee: Employee): Promise<ApiResponse<Employee>> {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(employee),
      });

      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: '従業員の登録に失敗しました（通信エラー）',
      };
    }
  }

  /**
   * Gets all employees.
   * 
   * Fetches all employees from the backend.
   * 
   * @returns {Promise<ApiResponse<Employee[]>>} Response with array of all employees
   * @async
   * 
   * @example
   * ```typescript
   * const result = await employeeService.getAllEmployees();
   * if (result.success && result.data) {
   *   const employees = result.data;
   * }
   * ```
   */
  async getAllEmployees(): Promise<ApiResponse<Employee[]>> {
    try {
      const response = await fetch(this.baseUrl, {
        headers: this.getHeaders(),
      });
      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: '従業員一覧の取得に失敗しました（通信エラー）',
      };
    }
  }

  /**
   * Searches for employees by query.
   * 
   * Searches employees by employee code or name.
   * If query is empty, returns all employees.
   * 
   * @param query - Search query (employee code or name)
   * @returns {Promise<ApiResponse<Employee[]>>} Response with matching employees
   * @async
   * 
   * @example
   * ```typescript
   * // Search for employees with code or name containing "123"
   * const result = await employeeService.searchEmployees('123');
   * ```
   */
  async searchEmployees(query: string): Promise<ApiResponse<Employee[]>> {
    try {
      const url = query ? `${this.baseUrl}/search?query=${encodeURIComponent(query)}` : this.baseUrl;
      const response = await fetch(url, {
        headers: this.getHeaders(),
      });
      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: '従業員の検索に失敗しました（通信エラー）',
      };
    }
  }

  /**
   * Gets an employee by employee code.
   * 
   * Fetches a specific employee by their employee code.
   * 
   * @param empCode - Employee code (6 digits)
   * @returns {Promise<ApiResponse<Employee>>} Response with employee data
   * @async
   * 
   * @example
   * ```typescript
   * const result = await employeeService.getEmployeeByEmpCode('123456');
   * ```
   */
  async getEmployeeByEmpCode(empCode: string): Promise<ApiResponse<Employee>> {
    try {
      const response = await fetch(`${this.baseUrl}/${empCode}`, {
        headers: this.getHeaders(),
      });
      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: '従業員情報の取得に失敗しました（通信エラー）',
      };
    }
  }

  /**
   * Updates an existing employee.
   * 
   * Updates employee information by employee code.
   * Password is optional and only updated if provided.
   * 
   * @param empCode - Employee code to update
   * @param employee - Updated employee data
   * @returns {Promise<ApiResponse<Employee>>} Response with updated employee data
   * @async
   * 
   * @example
   * ```typescript
   * const updated = {
   *   empCode: '123456',
   *   name: '山田太郎',
   *   userType: '管理者',
   *   password: 'NewPassword123' // Optional
   * };
   * const result = await employeeService.updateEmployee('123456', updated);
   * ```
   */
  async updateEmployee(empCode: string, employee: Employee): Promise<ApiResponse<Employee>> {
    try {
      const response = await fetch(`${this.baseUrl}/${empCode}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(employee),
      });

      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: '従業員情報の更新に失敗しました（通信エラー）',
      };
    }
  }

  /**
   * Deletes an employee.
   * 
   * Deletes an employee by employee code.
   * WARNING: This operation is irreversible.
   * 
   * @param empCode - Employee code to delete
   * @returns {Promise<ApiResponse<void>>} Response indicating success or failure
   * @async
   * 
   * @example
   * ```typescript
   * const result = await employeeService.deleteEmployee('123456');
   * ```
   */
  async deleteEmployee(empCode: string, deleteLinkedConstruction = false): Promise<ApiResponse<void>> {
    try {
      const q = deleteLinkedConstruction ? '?deleteLinkedConstruction=true' : '';
      const response = await fetch(`${this.baseUrl}/${encodeURIComponent(empCode)}${q}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });

      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: '従業員の削除に失敗しました（通信エラー）',
      };
    }
  }

  /**
   * Checks if an employee code already exists.
   * 
   * Used for validation before creating new employees.
   * 
   * @param empCode - Employee code to check
   * @returns {Promise<ApiResponse<{ exists: boolean }>>} Response with exists flag
   * @async
   * 
   * @example
   * ```typescript
   * const result = await employeeService.checkEmployeeCodeExists('123456');
   * if (result.data?.exists) {
   *   // Employee code already exists
   * }
   * ```
   */
  async checkEmployeeCodeExists(empCode: string): Promise<ApiResponse<{ exists: boolean }>> {
    try {
      const response = await fetch(`${this.baseUrl}/check-code/${empCode}`, {
        headers: this.getHeaders(),
      });
      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: '従業員コードの確認に失敗しました（通信エラー）',
      };
    }
  }

  /**
   * Gets employees filtered by user type.
   * 
   * Fetches all employees of a specific user type ('従業員' or '管理者').
   * 
   * @param userType - User type to filter by ('従業員' or '管理者')
   * @returns {Promise<ApiResponse<Employee[]>>} Response with filtered employees
   * @async
   * 
   * @example
   * ```typescript
   * // Get all administrators
   * const result = await employeeService.getEmployeesByUserType('管理者');
   * ```
   */
  async getEmployeesByUserType(userType: string): Promise<ApiResponse<Employee[]>> {
    try {
      const response = await fetch(`${this.baseUrl}/by-type/${userType}`, {
        headers: this.getHeaders(),
      });
      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: '従業員の取得に失敗しました（通信エラー）',
      };
    }
  }

  /**
   * Changes an employee's password.
   * 
   * Updates employee password after verifying current password.
   * 
   * @param empCode - Employee code
   * @param currentPassword - Current password for verification
   * @param newPassword - New password to set
   * @returns {Promise<ChangePasswordResponse>} Response indicating success or failure
   * @async
   * 
   * @example
   * ```typescript
   * const result = await employeeService.changePassword(
   *   '123456',
   *   'OldPassword123',
   *   'NewPassword123'
   * );
   * ```
   */
  async changePassword(empCode: string, currentPassword: string, newPassword: string): Promise<ChangePasswordResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/change-password`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ empCode, currentPassword, newPassword })
      });
      const result = await response.json();
      return result;
    } catch (error) {
      return { success: false, message: 'パスワードの変更に失敗しました（通信エラー）' };
    }
  }

  /**
   * Gets employees for salary/payslip purposes.
   * 
   * Fetches employees that have salary data, optionally filtered by query.
   * Used for populating employee dropdowns in payslip generation.
   * 
   * @param query - Optional search query for filtering
   * @returns {Promise<ApiResponse<Employee[]>>} Response with salary employees
   * @async
   * 
   * @example
   * ```typescript
   * // Get all salary employees
   * const result = await employeeService.getSalaryEmployees();
   * 
   * // Get salary employees matching query
   * const result = await employeeService.getSalaryEmployees('123');
   * ```
   */
  async getSalaryEmployees(query?: string): Promise<ApiResponse<Employee[]>> {
    try {
      const url = query ? `${this.baseUrl}/salary-employees?query=${encodeURIComponent(query)}` : `${this.baseUrl}/salary-employees`;
      const response = await fetch(url, {
        headers: this.getHeaders(),
      });
      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: '給与データの従業員一覧の取得に失敗しました（通信エラー）',
      };
    }
  }
}

/**
 * Export singleton instance of EmployeeService.
 * 
 * Use this instance throughout the application to interact
 * with the employee API.
 * 
 * @example
 * ```typescript
 * import EmployeeService from './services/EmployeeService';
 * 
 * // Use the service
 * const employees = await EmployeeService.getAllEmployees();
 * ```
 */
export default new EmployeeService();
